/**
 * EXPERT NETWORKS (EXPERT-NETWORKS.md §5, E4) — the Expert Networks that review this room's requests, in the room's
 * settings. Consult = their rules ride into every run / Ask / Do; Gate = and rules that must hold are hard limits.
 * Network-wide ones (set by the workspace owner) are inherited — a room can switch one off for itself.
 * Additive section; no existing control changes. A COMPANY-WIDE expert (EXPERT-ROOMS.md §14 — another workspace of the
 * company, subscribed) shows "company"; its rules are read-only here, and every change carries its ``expertTenantId``.
 */
import React, { useState } from 'react';

export const EXPERT_MODES = [
  { id: 'consult', label: 'Consult', hint: 'Their rules guide every request in this room' },
  { id: 'gate', label: 'Gate', hint: 'As consult — and rules that must hold stop work that would break them' },
];

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

export default function RoomExperts({ value, onChange }) {
  const v = value || {};
  const plugins = v.plugins || { inForce: [], network: [], room: [] };
  const inForce = plugins.inForce || [];
  const roomOff = (plugins.room || []).filter((p) => p.mode === 'off');
  const candidates = (v.candidates || []).filter((c) => !inForce.some((p) => p.expertNetworkId === c.networkId));
  const [picked, setPicked] = useState([]);       // MULTI-select (§18): several experts plugged in one save
  const [find, setFind] = useState('');
  const [mode, setMode] = useState('consult');
  const [everyRoom, setEveryRoom] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const send = async (req) => {
    if (!onChange) return;
    setBusy(true);
    setErr('');
    try {
      await onChange(req);
    } catch (e) {
      setErr((e && e.message) || "Couldn't save that.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 text-[12px]" data-testid="room-experts">
      <p className="text-[11px] text-gray-500">
        Expert Networks review this room&apos;s requests. Their rules travel with every run, Ask and Do.
      </p>
      {inForce.length === 0 ? (
        <p className="text-[11px] text-gray-400" data-testid="room-experts-empty">No Expert Network reviews this room yet.</p>
      ) : (
        <ul className="space-y-1">
          {inForce.map((p) => {
            const networkWide = p.inherited || p.tier === 'network';
            const canEdit = networkWide ? v.canSetNetwork : v.canSet;
            return (
              <li key={p.expertNetworkId} className="flex items-center gap-2" data-testid={`room-expert-${p.expertNetworkId}`}>
                <span className="font-medium text-gray-800">{p.name || p.expertNetworkId}</span>
                {p.companyWide || p.expertTenantId ? (
                  <span className="rounded bg-violet-50 px-1.5 py-px text-[10px] text-violet-700" data-testid="room-expert-company"
                    title="Shared by another workspace — its rules are read-only here">
                    shared
                  </span>
                ) : null}
                {networkWide ? (
                  <span className="rounded bg-gray-100 px-1.5 py-px text-[10px] text-gray-600" title="Set for every room of this network">
                    every room
                  </span>
                ) : null}
                {canEdit ? (
                  <select value={p.mode} disabled={busy} aria-label="How it reviews"
                    onChange={(e) => send({ expertNetworkId: p.expertNetworkId, mode: e.target.value, tier: networkWide ? 'network' : 'room',
                      ...(p.expertTenantId ? { expertTenantId: p.expertTenantId } : {}), ...(p.stage ? { stage: p.stage } : {}),
                      ...(p.profileId ? { profileId: p.profileId } : {}) })}
                    className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px]">
                    {EXPERT_MODES.map((m) => <option key={m.id} value={m.id} title={m.hint}>{m.label}</option>)}
                  </select>
                ) : (
                  <span className="rounded bg-indigo-50 px-1.5 py-px text-[10px] font-medium text-indigo-700" data-testid="room-expert-mode">
                    {(EXPERT_MODES.find((m) => m.id === p.mode) || EXPERT_MODES[0]).label}
                  </span>
                )}
                {/* EXPERT-ROOMS §14b — a shared expert with a subscription form: the room picks one of the workspace's
                    PROFILES (prefilled answers; set up under Workspace settings → Experts) */}
                {(p.profiles || []).length ? (
                  canEdit ? (
                    <select value={p.profileId || ''} disabled={busy} aria-label="Subscription profile"
                      data-testid={`room-expert-profile-${p.expertNetworkId}`}
                      onChange={(e) => send({ expertNetworkId: p.expertNetworkId, mode: p.mode, tier: networkWide ? 'network' : 'room',
                        expertTenantId: p.expertTenantId, ...(e.target.value ? { profileId: e.target.value } : {}) })}
                      className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px]">
                      {p.profiles.length > 1 || !p.profileId ? <option value="">{p.profiles.length === 1 ? p.profiles[0].name : 'Choose a profile…'}</option> : null}
                      {p.profiles.map((x) => <option key={x.profileId} value={x.profileId}>{x.name}</option>)}
                    </select>
                  ) : (
                    <span className="text-[10px] text-gray-500" data-testid="room-expert-profile">
                      {(p.profiles.find((x) => x.profileId === p.profileId) || (p.profiles.length === 1 ? p.profiles[0] : null))?.name || 'no profile'}
                    </span>
                  )
                ) : null}
                {/* §14c — the platform setup made from the profile (e.g. the AWS organization): ready, or not yet */}
                {(() => {
                  const pick = (p.profiles || []).find((x) => x.profileId === p.profileId) || ((p.profiles || []).length === 1 ? p.profiles[0] : null);
                  if (!pick?.setup) return null;
                  return pick.setup === 'ready'
                    ? <span className="rounded bg-emerald-50 px-1.5 py-px text-[10px] font-medium text-emerald-700" data-testid={`room-expert-setup-${p.expertNetworkId}`}>access ready</span>
                    : <a href="/workspace/settings" className="rounded bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-800 hover:underline"
                      title="Finish the setup under Workspace settings → Tenant → Experts" data-testid={`room-expert-setup-${p.expertNetworkId}`}>setup not finished</a>;
                })()}
                {/* EXPERT-ROOMS §15 — this workspace's own expert answers from Training (dev) or its QA team; another
                    workspace's always answers from Production */}
                {canEdit && !(p.companyWide || p.expertTenantId) ? (
                  <select value={p.stage === 'qa' ? 'qa' : 'dev'} disabled={busy} aria-label="Answers from"
                    data-testid={`room-expert-stage-${p.expertNetworkId}`}
                    onChange={(e) => send({ expertNetworkId: p.expertNetworkId, mode: p.mode, tier: networkWide ? 'network' : 'room',
                      ...(e.target.value === 'qa' ? { stage: 'qa' } : {}) })}
                    className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px]">
                    <option value="dev">answers from Training</option>
                    <option value="qa">answers from QA</option>
                  </select>
                ) : p.stage === 'qa' ? (
                  <span className="rounded bg-amber-50 px-1.5 py-px text-[10px] font-semibold text-amber-700">QA</span>
                ) : null}
                {networkWide && v.canSet ? (
                  <button type="button" disabled={busy} className={`${btn} text-gray-600 hover:bg-gray-100`}
                    onClick={() => send({ expertNetworkId: p.expertNetworkId, mode: 'off', tier: 'room' })}>
                    Switch off here
                  </button>
                ) : null}
                {canEdit ? (
                  <button type="button" disabled={busy} className={`${btn} text-rose-700 hover:bg-rose-50`}
                    data-testid={`room-expert-remove-${p.expertNetworkId}`}
                    onClick={() => send({ expertNetworkId: p.expertNetworkId, mode: 'remove', tier: networkWide ? 'network' : 'room' })}>
                    {networkWide ? 'Remove from every room' : 'Remove'}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {roomOff.length && v.canSet ? (
        <ul className="space-y-1">
          {roomOff.map((p) => (
            <li key={p.expertNetworkId} className="flex items-center gap-2 text-[11px] text-gray-500">
              {p.name || p.expertNetworkId} — switched off in this room
              <button type="button" disabled={busy} className={`${btn} text-indigo-700 hover:bg-indigo-50`}
                onClick={() => send({ expertNetworkId: p.expertNetworkId, mode: 'remove', tier: 'room' })}>
                Switch back on
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {(v.canSet || v.canSetNetwork) ? (
        <div className="space-y-1.5 border-t border-gray-100 pt-2" data-testid="room-expert-picker">
          <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search experts — e.g. AWS"
            data-testid="room-expert-search" aria-label="Search experts"
            className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]" />
          {(() => {
            const q = find.trim().toLowerCase();
            const shown = candidates.filter((c) => !q || `${c.name || ''} ${c.networkId}`.toLowerCase().includes(q));
            return shown.length ? (
              <ul className="max-h-40 space-y-0.5 overflow-y-auto" data-testid="room-expert-options">
                {shown.map((c) => (
                  <li key={c.networkId}>
                    <label className="flex items-center gap-2 text-[11px] text-gray-700">
                      <input type="checkbox" checked={picked.includes(c.networkId)} data-testid={`room-expert-option-${c.networkId}`}
                        onChange={(e) => setPicked((cur) => (e.target.checked ? [...cur, c.networkId] : cur.filter((x) => x !== c.networkId)))} />
                      <span className="font-medium">{c.name || c.networkId}</span>
                      {c.companyWide ? <span className="rounded bg-violet-50 px-1 py-px text-[10px] text-violet-700">shared</span> : null}
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-gray-400" data-testid="room-expert-none">
                {q ? `No subscribed or own expert matches “${find.trim()}”.` : 'Every available expert is already here.'}
              </p>
            );
          })()}
          <p className="text-[11px] text-gray-500">
            Need another? <a href="/registry?tab=experts" className="text-indigo-700 hover:underline" data-testid="room-expert-find-more">
              Find experts</a> — subscribe there, then it shows up here.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select value={mode} onChange={(e) => setMode(e.target.value)} aria-label="How it reviews"
              data-testid="room-expert-pick-mode" className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px]">
              {EXPERT_MODES.map((m) => <option key={m.id} value={m.id} title={m.hint}>{m.label}</option>)}
            </select>
            {v.canSetNetwork ? (
              <label className="flex items-center gap-1 text-[11px] text-gray-600">
                <input type="checkbox" checked={everyRoom} onChange={(e) => setEveryRoom(e.target.checked)} />
                every room of this network
              </label>
            ) : null}
            <button type="button" disabled={busy || !picked.length || (!everyRoom && !v.canSet)} data-testid="room-expert-add"
              className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
              onClick={async () => {
                for (const id of picked) {
                  const c = candidates.find((x) => x.networkId === id);
                  // eslint-disable-next-line no-await-in-loop
                  await send({ expertNetworkId: id, mode, tier: everyRoom ? 'network' : 'room',
                    ...(c && c.expertTenantId ? { expertTenantId: c.expertTenantId } : {}) });
                }
                setPicked([]); setFind('');
              }}>
              {picked.length > 1 ? `Plug in ${picked.length}` : 'Plug in'}
            </button>
          </div>
        </div>
      ) : null}
      {err ? <p className="text-[11px] text-rose-600">{err}</p> : null}
    </div>
  );
}
